import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SiteActions, SiteSelectors } from '@next/core-lib/site';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { debounceTime, map, take } from 'rxjs/operators';

import { Identification, Location, SalesArea } from './models/relationships.model';
import { SiteState } from '../shared/models/siteState.model';
import {
  autoSuggestProfiles,
  clearProfileResult,
  initAuthorization,
  searchProfilesRequest,
} from './store/actions/profile.actions';
import {
  selectApplicationGroups,
  selectApplications,
  selectautoSuggestedProfiles,
  selectB2CProfile,
  selectProfile,
  selectProfileRelationships,
  selectSearchLoadResult,
} from './store/selectors/profile.selectors';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['profile.component.scss'],
  standalone: false,
})
export class ProfileComponent implements OnInit, OnDestroy {
  pageTitle$ = this.store.select(SiteSelectors.selectPageTitle);
  profile$ = this.store.select(selectProfile);
  applications$ = this.store.select(selectApplications);
  applicationGroups$ = this.store.select(selectApplicationGroups);
  relationships$ = this.store.select(selectProfileRelationships);
  b2cProfile$ = this.store.select(selectB2CProfile);
  options$ = this.store.select(selectautoSuggestedProfiles);
  user$ = this.store.select(UserSelectors.selectUser);
  showNotFound: boolean;

  private subscription: Subscription;

  form = this.formBuilder.group({
    search: [],
  });

  public profileIdentifications: Identification[];

  profileIdentifications$ = this.relationships$.pipe(
    map((profileRelationships) => {
      if (profileRelationships) {
        const salesArea = profileRelationships.reduce(
          (accumulator: SalesArea[], value) => [...accumulator, ...value.salesAreas],
          [] as SalesArea[],
        );

        const locations = salesArea?.reduce(
          (accumulator: Location[], value) => [...accumulator, ...value.locations],
          [] as Location[],
        );

        return locations?.reduce(
          (accumulator: Identification[], value) => [...accumulator, ...value.identifications],
          [] as Identification[],
        );
      } else {
        return [];
      }
    }),
  );

  constructor(
    private readonly store: Store<SiteState>,
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
  ) {
    this.store.select(selectSearchLoadResult).subscribe((result) => {
      // result is undefined on initial load, we don't want the 404 on page load
      if (result !== undefined) {
        this.showNotFound = !result;
      }
    });
  }

  ngOnInit() {
    this.store.dispatch(SiteActions.updatePageTitle({ pageTitle: 'profile' }));
    this.profile$.subscribe((profile) => {
      if (profile) {
        this.store.dispatch(initAuthorization({ profileId: profile.profileId }));
      }
    });

    const email = this.activatedRoute.snapshot.queryParamMap.get('email');

    if (this.isEmail(email)) {
      this.form.controls.search.setValue(email);
      this.store.dispatch(searchProfilesRequest({ searchtext: email }));
    }

    this.subscription = this.form.controls.search.valueChanges
      .pipe(
        map((i: unknown) => i),
        debounceTime(550),
      )
      .subscribe((searchtext: string) => {
        if (searchtext?.length >= 4) {
          this.store.dispatch(autoSuggestProfiles({ searchtext }));
        }
      });
  }

  searchProfiles() {
    const email = this.form.controls.search.value?.trim();

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { email },
      queryParamsHandling: 'merge',
    });
    if (this.isEmail(email)) {
      this.store.dispatch(searchProfilesRequest({ searchtext: email }));
    }
  }

  clearSearchbox() {
    this.form.controls.search.setValue('');
    this.store.dispatch(clearProfileResult());
  }

  useMyEmail() {
    this.user$.pipe(take(1)).subscribe((user) => {
      if (user?.email) {
        this.form.controls.search.setValue(user.email);
        this.searchProfiles();
      }
    });
  }

  isEmail(email: string): boolean {
    const regexp = new RegExp(
      //eslint-disable-next-line no-useless-escape
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/, // eslint-disable-line max-len
    );
    return regexp.test(email);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
